import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { getRecolteurs } from '../services/api';
import { notificationService } from '../utils/notifications';
import './ListeMontantsPrint.css';

const ListeRecolteursPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [recolteurs, setRecolteurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState('');

  // Récupérer les paramètres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const association = params.get('association') || '';
    setSearchTerm(search);
    setSelectedAssociation(association);
  }, [location]);

  useEffect(() => {
    const loadRecolteurs = async () => {
      try {
        const response = await getRecolteurs();
        setRecolteurs(response.data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des récolteurs:', error);
        notificationService.error('Erreur', 'Impossible de charger les récolteurs');
      } finally {
        setLoading(false);
      }
    };

    loadRecolteurs();
  }, []);

  const handlePrint = () => {
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;

    try {
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `Liste_Recolteurs${selectedAssociation ? `_${selectedAssociation.replace(/\s+/g, '_')}` : ''}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: printRef.current.scrollWidth,
          height: printRef.current.scrollHeight,
          windowWidth: printRef.current.scrollWidth,
          allowTaint: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.print-header', '.print-table'] }
      };

      await html2pdf().set(opt).from(printRef.current).save();
      notificationService.success('PDF généré', 'Le fichier PDF a été téléchargé avec succès.');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      notificationService.error('Erreur', 'Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    window.close();
    if (!document.hidden) {
      navigate('/admin');
    }
  };

  // Filtrer les récolteurs
  const filteredRecolteurs = recolteurs.filter(recolteur => {
    // Filtre par association
    if (selectedAssociation && recolteur.association !== selectedAssociation) {
      return false;
    }
    
    // Filtre par recherche
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      recolteur.username?.toLowerCase().includes(search) ||
      recolteur.nom_complet?.toLowerCase().includes(search) ||
      recolteur.telephone?.toLowerCase().includes(search) ||
      recolteur.matricule?.toLowerCase().includes(search) ||
      recolteur.association?.toLowerCase().includes(search) ||
      recolteur.commune?.toLowerCase().includes(search) ||
      recolteur.centre_sante?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des récolteurs...</p>
      </div>
    );
  }

  return (
    <div className="liste-montants-print-page" style={{ display: 'block', visibility: 'visible' }}>
      {/* Barre d'outils - masquée à l'impression */}
      <div className="print-toolbar" style={{ display: 'flex' }}>
        <div className="toolbar-left">
          <h2>Liste des Récolteurs - Impression{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={handlePrint}>
            🖨️ Imprimer
          </button>
          <button className="btn btn-success" onClick={handleDownloadPDF}>
            📥 Télécharger PDF
          </button>
          <button className="btn btn-secondary" onClick={handleClose}>
            ✕ Fermer
          </button>
        </div>
      </div>

      {/* Contenu à imprimer */}
      <div ref={printRef} className="print-content" style={{ display: 'block', visibility: 'visible', background: 'white' }}>
        {/* En-tête */}
        <div className="print-header">
          <div className="header-content-wrapper">
            <div className="header-logo">
              <img src="/logo.png" alt="Logo" className="logo-img" />
            </div>
            <div className="header-text">
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <h2>Liste des Récolteurs{selectedAssociation ? ` - ${selectedAssociation}` : ''}</h2>
              <div className="header-info">
                <p className="print-date">
                  <strong>Date d'édition :</strong> {new Date().toLocaleDateString('fr-FR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                {selectedAssociation && (
                  <p className="print-date" style={{ marginTop: '5px' }}>
                    <strong>Association :</strong> {selectedAssociation}
                  </p>
                )}
                <p className="print-date" style={{ marginTop: '5px' }}>
                  <strong>Total :</strong> {filteredRecolteurs.length} récolteur(s)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="print-table-container">
          <table className="print-table">
            <thead>
              <tr>
                <th>N°</th>
                <th>Nom d'utilisateur</th>
                <th>Nom complet</th>
                <th>Téléphone</th>
                <th>Matricule</th>
                <th>Commune</th>
                <th>Centre de santé</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecolteurs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {searchTerm || selectedAssociation ? 'Aucun récolteur trouvé' : 'Aucun récolteur enregistré'}
                  </td>
                </tr>
              ) : (
                filteredRecolteurs.map((recolteur, index) => (
                  <tr key={recolteur.id}>
                    <td>{index + 1}</td>
                    <td><strong>{recolteur.username || '-'}</strong></td>
                    <td>{recolteur.nom_complet || '-'}</td>
                    <td>{recolteur.telephone || '-'}</td>
                    <td>{recolteur.matricule || '-'}</td>
                    <td>{recolteur.commune || '-'}</td>
                    <td>{recolteur.centre_sante || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pied de page */}
        <div className="print-footer">
          <p>Document généré par MUTU NDE ESPOIR ASBL - Système de gestion</p>
        </div>
      </div>
    </div>
  );
};

export default ListeRecolteursPrint;

